<template>
  <div class="proposal">
    <div class="proposal__hero">
      <header class="proposal__header">
        <div class="componata-container">
          <div class="grid-2-middle">
            <div class="col">
              <a href="/" title="Homepage" class="proposal__logo"><img src="@/assets/images/logo-w.svg" alt="Componata"></a> <small class="proposal__logo-tagline">Für Ihr Unternehmen</small>
            </div>

            <div class="col u-text-right">
              <div class="proposal__phone">
                <i class="icon icon-phone"></i> 0341 9899 208
              </div>
            </div>
          </div>
        </div>
      </header>

      <div v-if="data" class="proposal__cover">
        <div class="componata-container">
          <div class="grid-2_sm-1-noBottom">
            <div class="col">
              <div class="proposal__hero-content">
                <h2 class="proposal__title">
                  <slot v-if="data.acf.title">
                    {{ data.acf.title }}
                  </slot>

                  <slot v-else>
                    Maßgeschneidertes Präsent für {{ data.title.rendered }}
                  </slot>
                </h2>

                <div
                  v-if="data.acf.description"
                  class="proposal__description"
                >
                  <p v-html="data.acf.description"></p>
                </div>
              </div>
            </div>

            <div class="col">
              <img
                src="@/assets/images/hero-presents-collage.png"
                srcset="@/assets/images/hero-presents-collage.png 1x, @/assets/images/hero-presents-collage@2x.png 2x"
                class="proposal__hero-image"
                alt="Präsente von Componata"
              >
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="proposal__cover --is-placeholder"
      >
        <div class="componata-container">
          <div class="grid-2_sm-1-noBottom">
            <div class="col">
              <div class="proposal__hero-content">
                <h2 class="proposal__title"><span></span> <span></span></h2>

                <div class="proposal__description">
                  <p><span></span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Section
      v-if="data"
      id="products-section"
    >
      <div class="componata-container --is-narrow">
        <SectionHeader>
          <template v-slot:title>
            <slot v-if="data.acf.catalog_title">{{ data.acf.catalog_title }}</slot>
            <slot v-else>Präsenteoptionen</slot>
          </template>

          <template v-slot:description>
            <p v-if="data.acf.catalog_description">{{ data.acf.catalog_description }}</p>
            <p v-else>Unsere Präsentboxen beinhalten ausschließlich ausgewählte Delikatessen aus aller Welt. Hochwertige und exklusive Feinkost für ganz besondere Menschen.</p>
          </template>
        </SectionHeader>
      </div>

      <div v-if="catalog" class="componata-container">
        <PresentContents
          class="proposal__present"
          v-for="(present, index) in catalog"
          :key="index"
          :extended="true"
          :sku="present.sku"
          :type="present.type"
          :name="present.custom_name || present.name"
          :customPrices="present.custom_prices"
          :products="present.products"
          :notes="present.notes"
        >
          <template v-if="present.cta.text && present.cta.link" v-slot:cta>
            <Button :href="present.cta.link" appearance="primary">{{ present.cta.text }}</Button>
          </template>
        </PresentContents>
      </div>
    </Section>

    <TheFooter />
  </div>
</template>

<script>
import axios from 'axios';

import { logicMixin } from '@/mixins/LogicMixin';

import Section from '@/components/PresentPage/Section';
import SectionHeader from '@/components/PresentPage/SectionHeader';
import PresentContents from '@/components/Pages/PresentContents';
import Button from '@/components/Common/Button';
import TheFooter from '@/components/TheFooter';

export default {
  name: 'ProposalPage',
  mixins: [logicMixin],
  components: {
    Section,
    SectionHeader,
    PresentContents,
    Button,
    TheFooter
  },

  data: function () {
    return {
      data: null,
      catalog: []
    };
  },

  created () {
    this.$store.commit('setGlobalLoading', true);

    // Get the data from the API
    axios
      .get(process.env.VUE_APP_BASE_URL + '/wp-json/wp/v2/proposal_pages/' + this.$route.params.id)
      .then((proposalRes) => {
        if (proposalRes.data) {
          this.data = proposalRes.data;

          // Prepare the actual (real) level 1 boxes to get from the API
          const level1Presents = [];

          proposalRes.data.acf.catalog.forEach(item => {
            if (item.type === 'level1') {
              level1Presents.push(item.level1);
            }
          });

          // Perform API calls for getting level 1 and level 3 stuff
          Promise.all([
            axios.get(process.env.VUE_APP_BASE_URL + '/wp-json/geschenkbox/v1/get_level_1_fast?selectedId=' + level1Presents.join()),
            axios.get(process.env.VUE_APP_BASE_URL + '/wp-json/geschenkbox/v1/get_level_3_fast')
          ])
            .then(([level1Res, level3Res]) => {
              this.$store.commit('setListItemsLevel1', this.extendLvl1Req(level1Res.data));
              this.$store.commit('setListItemsLevel3', this.extendLvl3Req(level3Res.data));

              // Create the virtual catalog for the proposal based on the actual level 1 presents and "virtual presents" based on level 3
              proposalRes.data.acf.catalog.forEach(item => {
                let toPush = {};

                // Level 1, else it is virtual
                if (item.type === 'level1') {
                  toPush = {
                    ...this.$store.getters.getListItemsLevel1Unfiltered.filter((present) => present.id === item.level1)[0],
                    custom_name: item.name,
                    custom_prices: item.prices
                  };
                } else {
                  toPush = {
                    custom_name: item.name,
                    custom_prices: item.prices,
                    products: this.$store.getters.getListItemsLevel3All.filter((level3Product) => item.level3.includes(level3Product.id.toString()))
                  };
                }

                this.catalog.push({
                  ...toPush,
                  type: item.type,
                  notes: item.notes,
                  cta: {
                    text: item.cta_text,
                    link: item.cta_link
                  }
                });
              });

              if (this.$route.name === 'ProposalPageProductDetail') {
                this.$store.getters.getListItemsLevel3All.filter((level3Product) => {
                  if (level3Product.id === Number(this.$route.params.productId)) {
                    this.$store.commit('setModalItem', level3Product);
                  }
                });
                this.$store.commit('setModalOpen', true);
                this.$store.commit('setModalCustomOpen', false);
              }

              this.$store.commit('setGlobalLoading', false);
            });
        }
      })
      .catch(() => {
        this.$router.push(this.cLink('/404'));
      });
  },

  watch: {
    $route (to) {
      if (to.name === 'ProposalPageDeliveryDetail') {
        this.$store.commit('setModalCustomOpen', true);
        this.$store.commit('setModalOpen', false);
      } else if (this.$route.name === 'ProposalPageProductDetail') {
        this.$store.getters.getListItemsLevel3All.filter((level3Product) => {
          if (level3Product.id === Number(this.$route.params.productId)) {
            this.$store.commit('setModalItem', level3Product);
          }
        });
        this.$store.commit('setModalOpen', true);
        this.$store.commit('setModalCustomOpen', false);
      } else {
        this.$store.commit('setModalOpen', false);
        this.$store.commit('setModalCustomOpen', false);
      }
    },
  },

  metaInfo () {
    if (this.data) {
      return {
        title: this.data.title.rendered
      };
    }
  }
};
</script>

<style lang="scss" scoped>
/**
 * ProposalPage
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc Proposal Page (Customer Leads)
 * @since 2021-11-16
**/

$component: 'proposal';

.#{$component} {
  background-color: $white;

  &__header {
    @include padding(map-get($spacers, 6) 0);
  }

  &__logo {
    display: inline-block;
    height: 40px;
    @include margin-right(map-get($spacers, 2));

    img {
      display: block;
      width: auto;
      height: 100%;
    }
  }

  &__logo-tagline {
    display: inline-block;

    // margin: 3px 15px;
    color: $text-dimmed;
  }

  &__phone {
    position: relative;
    display: inline-block;
    align-items: center;
    z-index: 4;
    @include padding(map-get($spacers, 1));
    @include padding-right(map-get($spacers, 2));
    border-radius: 50px;
    background-color: #182028;
    text-align: right;
    line-height: 1;

    .icon {
      @include margin-right(map-get($spacers, 1));
      @include padding(map-get($spacers, 1));
      vertical-align: sub;
      border-radius: 50%;
      background-color: $yellow;
      color: $ink;
      @include font-size($font-size-base * 1.4);
    }
  }

  &__hero {
    position: relative;
    height: 70vh;
    max-height: 70vh;
    overflow: hidden;
    background:
      url('~@/assets/images/bgr-tickling-stars.svg') -120% 5% no-repeat,
      linear-gradient(251.53deg, #182028 15.52%, #212B36 79.84%);
    // background-size: 80%;
    color: $sky-lighter;
  }

  &__hero-content {
    width: 80%;
    @include padding-top(map-get($spacers, 6));
  }

  &__hero-image {
    margin-top: -40%;
    z-index: 0;
  }

  &__title {
    @include font-size($h1-font-size * 1.4);
    line-height: $headings-line-height;
  }

  &__description {
    @include margin-top(map-get($spacers, 4));
    color: $ink-lightest;
  }

  &__present {
    & + & {
      @include margin-top(map-get($spacers, 6));
    }
  }

  // Placehoder
  .--is-placeholder {
    @include placeholder();

    .#{$component} {
      &__title span {
        display: block;
        @include rfs($h1-font-size, height);
        @include margin-bottom(map-get($spacers, 2));
        background-color: $sky;

        &:last-child {
          width: 60%;
        }
      }

      &__description p span {
        display: block;
        width: 70%;
        @include rfs($font-size-base, height);
        background-color: $ink-lightest;
      }
    }
  }

  /** RESPONSIVENESS **/

  // ——————————————————————————————
  // Max LG
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-lg')) {
    &__hero-image {
      margin-top: -45%;
      // margin-left: -20%;
    }
  }

  // ——————————————————————————————
  // Max MD
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-md')) {
    &__hero {
      background:
        url('~@/assets/images/bgr-tickling-stars.svg') -100% -10% no-repeat,
        linear-gradient(251.53deg, #182028 15.52%, #212B36 79.84%);
    }

    &__hero-image {
      width: 150%;
      margin-top: -30%;
    }
  }

  // ——————————————————————————————
  // Max SM
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-sm')) {
    &__hero {
      background:
        url('~@/assets/images/bgr-tickling-stars.svg') -50% top no-repeat,
        linear-gradient(251.53deg, #182028 15.52%, #212B36 79.84%);
    }

    &__hero-image {
      width: unset;
      margin-top: -20%;
    }
  }

  // ——————————————————————————————
  // Max XS
  // ——————————————————————————————
  @media (max-width: map-get($landing-page, 'viewport-xs')) {
    &__hero {
      background:
        url('~@/assets/images/bgr-tickling-stars.svg') -20% top no-repeat,
        linear-gradient(251.53deg, #182028 15.52%, #212B36 79.84%);
    }

    &__logo {
      height: 30px;
    }

    &__hero-content {
      @include padding-top(map-get($spacers, 3));
    }

    &__hero-image {
      width: 140%;
      margin-top: -10%;
      margin-left: -10%;
    }
  }
}
</style>
